import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { supabase } from '../../../lib/supabase';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useAuth } from '../../../context/AuthContext';
import { useTerms } from '../../../hooks/useTerms';
import { marked } from 'marked';

// 마크다운 → HTML 변환 함수
function markdownToHtml(md) {
  return marked(md || '', { breaks: true });
}

const CaseBriefForm = ({ open, onClose, onSuccess, initialData }) => {
  const [form, setForm] = useState({
    title: '',
    content: '',
    author_name: '',
    kor_summary: '',
    kor_content: '',
    category: '판례' // 기본값 설정
  });
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const [showPreview, setShowPreview] = useState(false);
  const { terms } = useTerms(); // 용어 데이터 가져오기
  const [subjects, setSubjects] = useState([]);

  // 과목 목록 추출
  useEffect(() => {
    if (terms && terms.length > 0) {
      // 중복 없는 과목 목록 추출
      const uniqueSubjects = [...new Set(terms.map(term => term.subject))];
      setSubjects(uniqueSubjects.sort());
    }
  }, [terms]);

  useEffect(() => {
    if (initialData) {
      setForm({
        title: initialData.title || '',
        content: initialData.content || '',
        author_name: initialData.author_name || '',
        kor_summary: initialData.kor_summary || '',
        kor_content: initialData.kor_content || '',
        category: initialData.category || '판례'
      });
    } else if (user) {
      // 새 글 작성 시 로그인한 사용자의 이메일로 자동 입력
      setForm({
        title: '',
        content: '',
        author_name: user.email, // user.id 대신 user.email 사용
        kor_summary: '',
        kor_content: '',
        category: '판례'
      });
    } else {
      setForm({ 
        title: '', 
        content: '', 
        author_name: '', 
        kor_summary: '', 
        kor_content: '',
        category: '판례'
      });
    }
  }, [initialData, open, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // ReactQuill용 핸들러
  const handleQuillChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // ReactQuill 모듈 설정 추가
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean'],
    ],
  };

  // ReactQuill 포맷 설정 추가
  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'align',
    'link', 'image',
    'table', 'table-cell', 'table-row', // 테이블 관련 포맷 추가
  ];

  const handleSubmit = async () => {
    setLoading(true);
    try {
      let result;
      
      // 필수 필드 검증
      if (!form.title) {
        throw new Error('제목은 필수 입력 항목입니다.');
      }
      
      // 사용자 정보 확인
      if (!user || !user.id) {
        throw new Error('로그인이 필요합니다.');
      }
      
      if (initialData?.id) {
        // 수정
        const { data, error } = await supabase
          .from('case_brief')
          .update({
            title: form.title,
            content: form.content || '',
            author_name: form.author_name,
            kor_summary: form.kor_summary, // 마크다운 원본 저장
            kor_content: form.kor_content, // 마크다운 원본 저장
            category: form.category
          })
          .eq('id', initialData.id)
          .select()
          .single();
        if (error) throw error;
        result = data;
      } else {
        // 새 글 작성
        const caseData = {
          title: form.title,
          content: form.content || '',
          author_name: form.author_name,
          kor_summary: form.kor_summary, // 마크다운 원본 저장
          kor_content: form.kor_content, // 마크다운 원본 저장
          created_at: new Date().toISOString(),
          user_id: user.id,
          views: 0,
          category: form.category
        };
        const { data, error } = await supabase
          .from('case_brief')
          .insert([caseData])
          .select()
          .single();
        if (error) throw error;
        result = data;
      }
      onSuccess(result);
      onClose();
    } catch (e) {
      alert('저장 중 오류: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{initialData ? 'Case Brief 수정' : 'Case Brief 등록'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            name="title"
            label="제목"
            value={form.title}
            onChange={handleChange}
            required
            fullWidth
          />
          <TextField
            name="author_name"
            label="작성자"
            value={form.author_name}
            onChange={handleChange}
            required
            fullWidth
            InputProps={{
              readOnly: true,
            }}
          />
          {/* 카테고리 선택 드롭다운 추가 */}
          <FormControl fullWidth>
            <InputLabel id="category-select-label">카테고리</InputLabel>
            <Select
              labelId="category-select-label"
              id="category-select"
              name="category"
              value={form.category}
              label="카테고리"
              onChange={handleChange}
            >
              <MenuItem value="판례">판례</MenuItem>
              {subjects.map((subject) => (
                <MenuItem key={subject} value={subject}>
                  {subject}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <div>
            <div style={{ marginBottom: 4, fontWeight: 600 }}>내용 (HTML 가능)</div>
            <ReactQuill
              value={form.content}
              onChange={value => handleQuillChange('content', value)}
              theme="snow"
              modules={modules}
              formats={formats}
              style={{ height: 200, marginBottom: 16 }}
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontWeight: 600 }}>
              사건 요약 <span style={{ color: '#888', fontWeight: 400, fontSize: 13 }}>(마크다운 지원)</span>
            </div>
            <TextField
              name="kor_summary"
              value={form.kor_summary}
              onChange={handleChange}
              multiline
              minRows={3}
              maxRows={8}
              fullWidth
              placeholder="마크다운 문법으로 입력하세요. 예: **굵게**, - 리스트, [링크](url), | 표 | 등"
            />
            {showPreview && (
              <Box sx={{ mt: 1, p: 1, border: '1px solid #eee', borderRadius: 1, background: '#fafafa' }}>
                <div
                  className="markdown-table"
                  dangerouslySetInnerHTML={{ __html: marked(form.kor_summary || '', { breaks: true }) }}
                />
              </Box>
            )}
          </div>
          <div>
            <div style={{ marginBottom: 4, fontWeight: 600 }}>
              법률 분석 <span style={{ color: '#888', fontWeight: 400, fontSize: 13 }}>(마크다운 지원)</span>
            </div>
            <TextField
              name="kor_content"
              value={form.kor_content}
              onChange={handleChange}
              multiline
              minRows={6}
              maxRows={16}
              fullWidth
              placeholder="마크다운 문법으로 입력하세요."
            />
            {showPreview && (
              <Box sx={{ mt: 1, p: 1, border: '1px solid #eee', borderRadius: 1, background: '#fafafa' }}>
                <div
                  className="markdown-table"
                  dangerouslySetInnerHTML={{ __html: marked(form.kor_content || '', { breaks: true }) }}
                />
              </Box>
            )}
          </div>
          <Button 
            variant="outlined" 
            onClick={() => setShowPreview(!showPreview)}
            sx={{ alignSelf: 'flex-start' }}
          >
            {showPreview ? '미리보기 숨기기' : '미리보기 보기'}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {initialData ? '수정' : '등록'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CaseBriefForm;
